import { evaluatePLAExpression, migratePLAExpression, validatePLAExpression } from "./PLAExpression";
import { PLADataService, PLAElementRecord, PLAProject, PLAProjectService, PLARelationRecord } from "./PLAState";

export type PLAExpressionMigrationChange = {
   location: string;
   before: string;
   after: string;
   elementTypeId: string;
   elementId?: string;
};

export type PLAExpressionMigrationPlan = {
   originalProject: PLAProject;
   originalElements: PLAElementRecord[];
   project: PLAProject;
   elements: PLAElementRecord[];
   changes: PLAExpressionMigrationChange[];
};

function sameExpressionValue(left: unknown, right: unknown): boolean {
   if (Object.is(left, right)) return true;
   if (Array.isArray(left) || Array.isArray(right)) return Array.isArray(left) && Array.isArray(right) && left.length === right.length && left.every((value, index) => sameExpressionValue(value, right[index]));
   if (!left || !right || typeof left !== "object" || typeof right !== "object") return false;
   const leftEntries = Object.entries(left);
   const rightEntries = Object.entries(right);
   return leftEntries.length === rightEntries.length && leftEntries.every(([key, value]) => Object.prototype.hasOwnProperty.call(right, key) && sameExpressionValue(value, Reflect.get(right, key)));
}

export function verifyPLAExpressionMigration(plan: PLAExpressionMigrationPlan, relations: PLARelationRecord[]): string[] {
   const warnings: string[] = [];
   const outcome = (expression: string, element: PLAElementRecord, migrated: boolean) => {
      try {
         const elements = migrated ? plan.elements : plan.originalElements;
         const target = migrated ? elements.find((candidate) => candidate.id === element.id) || element : element;
         return { failed: false, value: evaluatePLAExpression(expression, { project: migrated ? plan.project : plan.originalProject, element: target, elements, relations }), error: "" };
      } catch (error) {
         return { failed: true, value: undefined, error: error instanceof Error ? error.message : String(error) };
      }
   };
   for (const change of plan.changes) {
      const targets = plan.originalElements.filter((element) => change.elementId ? element.id === change.elementId : element.elementType === change.elementTypeId);
      for (const element of targets) {
         const before = outcome(change.before, element, false);
         const after = outcome(change.after, element, true);
         if (before.failed !== after.failed || !before.failed && !sameExpressionValue(before.value, after.value)) {
            throw new Error(`Migration changes the result of ${change.location} on ${element.name || element.id}. No expressions were saved.`);
         }
         if (before.failed) warnings.push(`${change.location} / ${element.name || element.id}: existing evaluation error is retained: ${before.error}`);
      }
   }
   return warnings;
}

export function createPLAExpressionMigrationPlan(project: PLAProject, elements: PLAElementRecord[]): PLAExpressionMigrationPlan {
   const changes: PLAExpressionMigrationChange[] = [];
   const convert = (expression: string, location: string, elementTypeId: string, elementId?: string): string => {
      try {
         const after = migratePLAExpression(expression);
         const validation = validatePLAExpression(after, { project, elementTypeId });
         if (!validation.valid) throw new Error(validation.error);
         if (after !== expression) changes.push({ location, before: expression, after, elementTypeId, elementId });
         return after;
      } catch (error) {
         throw new Error(`${location}: ${error instanceof Error ? error.message : String(error)}`);
      }
   };
   const elementTypes = Object.fromEntries(Object.entries(project.elementTypes).map(([elementTypeId, elementType]) => [
      elementTypeId,
      {
         ...elementType,
         attributes: Object.fromEntries(Object.entries(elementType.attributes).map(([attributeId, attribute]) => [
            attributeId,
            {
               ...attribute,
               ...(attribute.type === "expression" ? { expression: convert(attribute.expression || "", `${elementType.name}.${attribute.name}`, elementTypeId) } : {}),
               ...(attribute.alerts ? {
                  alerts: Object.fromEntries(Object.entries(attribute.alerts).map(([alertId, alert]) => [
                     alertId, { ...alert, booleanExpression: convert(alert.booleanExpression, `${elementType.name}.${attribute.name}.alert(${alertId})`, elementTypeId) }
                  ]))
               } : {})
            }
         ]))
      }
   ]));
   const migratedElements = elements.map((element) => {
      let changed = false;
      const attributes = Object.fromEntries(Object.entries(element.attributes).map(([id, attribute]) => {
         if (attribute.mode !== "expression") return [id, attribute];
         const expression = convert(attribute.expression, `${element.name || element.id}.${id}`, element.elementType, element.id);
         changed ||= expression !== attribute.expression;
         return [id, { ...attribute, expression }];
      }));
      return changed ? { ...element, attributes } : element;
   });
   return { originalProject: project, originalElements: elements, project: { ...project, elementTypes }, elements: migratedElements, changes };
}

export async function savePLAExpressionMigration(
   plan: PLAExpressionMigrationPlan,
   projectService: PLAProjectService,
   dataService: PLADataService,
   hasGlobalAdminAccess: () => boolean
): Promise<PLAProject> {
   if (!hasGlobalAdminAccess()) throw new Error("Global PLA administrator access is required");
   if (!plan.changes.length) return plan.originalProject;
   const records = await dataService.loadAllRecords();
   const currentElements = records.filter((record): record is PLAElementRecord => record.recordType === "ELEMENT");
   const relations = records.filter((record) => record.recordType === "RELATION");
   const currentProject = await projectService.loadProject(plan.project.id);
   if (currentProject._version !== plan.originalProject._version || JSON.stringify(currentProject.elementTypes) !== JSON.stringify(plan.originalProject.elementTypes)) {
      throw new Error("Project changed since the migration preview. Reload and preview again.");
   }
   if (JSON.stringify(currentElements.map((element) => [element.id, element._version, element.attributes]).sort()) !== JSON.stringify(plan.originalElements.map((element) => [element.id, element._version, element.attributes]).sort())) {
      throw new Error("Project elements changed since the migration preview. Reload and preview again.");
   }
   verifyPLAExpressionMigration({ ...plan, originalProject: currentProject, originalElements: currentElements }, relations);
   let savedDefinitions = false;
   let attemptedDefinitions = false;
   let savedElements = 0;
   try {
      if (!hasGlobalAdminAccess()) throw new Error("Global PLA administrator access is required");
      if (plan.changes.some((change) => !change.elementId)) {
         if (!currentProject._version) throw new Error("Project version is required for expression migration");
         attemptedDefinitions = true;
         await projectService.saveProject({ ...currentProject, elementTypes: plan.project.elementTypes }, currentProject._version);
         savedDefinitions = true;
      }
      for (const element of plan.elements) {
         if (!plan.changes.some((change) => change.elementId === element.id)) continue;
         if (!hasGlobalAdminAccess()) throw new Error("Global PLA administrator access is required");
         if (!element._version) throw new Error(`Element version is required for expression migration: ${element.name || element.id}`);
         await dataService.saveElement(element, element._version);
         savedElements++;
         const saved = await dataService.loadRecord(element.id);
         if (saved.recordType !== "ELEMENT" || JSON.stringify(saved.attributes) !== JSON.stringify(element.attributes)) throw new Error(`Could not verify saved expressions on ${element.name || element.id}`);
      }
      const savedProject = await projectService.loadProject(plan.project.id);
      if (JSON.stringify(savedProject.elementTypes) !== JSON.stringify(plan.project.elementTypes)) throw new Error("Could not verify saved project expressions");
      const finalRecords = await dataService.loadAllRecords();
      const finalElements = finalRecords.filter((record): record is PLAElementRecord => record.recordType === "ELEMENT");
      if (createPLAExpressionMigrationPlan(savedProject, finalElements).changes.length > 0) throw new Error("New legacy expressions appeared during migration. Reload and preview again to migrate the remaining expressions.");
      return savedProject;
   } catch (error) {
      throw new Error(`Migration stopped (${savedDefinitions ? "project definitions saved" : attemptedDefinitions ? "project definitions save unconfirmed" : "project definitions not saved"}, ${savedElements} element records saved). Unconfirmed writes may have completed. Reload and preview again to resume. ${error instanceof Error ? error.message : String(error)}`);
   }
}
